import { Injectable } from '@angular/core';

export interface AccessProblem {
  code: string;
  subscriptionStatus?: string;
  feature?: string;
  limit?: number;
  usage?: number;
  requestedIncrease?: number;
}

export interface PlatformSubscriptionState {
  status: string;
  source: string;
  givesAccess: boolean;
  trialEndsAt?: string;
  package?: { id?: number; name: string };
  price?: { id?: number; vatRate?: string; netAmount: string; billingInterval: string; intervalCount: number; currency: string } | null;
  pendingPlanChange?: {package: {name: string}; effectiveAt: string} | null;
  canChangePlan?: boolean;
  canResume?: boolean;
  canCancel?: boolean;
  cancelAt?: string | null;
  canPause?: boolean;
  pauseFee?: {netAmount: string; vatAmount: string; grossAmount: string; currency: string} | null;
  pauseStartsAt?: string | null;
  requiresPaymentToResume?: boolean;
  currentPeriodEnd?: string | null;
  nextChargeAt?: string | null;
}

export const ACCESS_PATH = '/admin/hozzaferes';

export function accessProblem(status: number, body: AccessProblem | null): AccessProblem | null {
  if (!body) return null;
  return (status === 423 && body.code === 'platform_subscription_inactive')
    || (status === 403 && body.code === 'feature_not_available')
    || (status === 409 && body.code === 'quota_exceeded') ? body : null;
}

export function accessMessage(problem: AccessProblem, subscription?: PlatformSubscriptionState | null) {
  if (subscription?.pauseStartsAt && subscription.givesAccess) return {
    icon: 'event', title: 'Szüneteltetés ütemezve',
    description: 'Az előfizetésed a már kifizetett időszak végéig használható.',
    detail: 'A szüneteltetés kezdetéig díjmentesen visszavonhatod a kérésedet.',
  };
  if (problem.code === 'feature_not_available') return {
    icon: 'workspace_premium', title: 'Ez a funkció nagyobb csomaggal érhető el',
    description: 'A jelenlegi Coursys-csomagod nem tartalmazza ezt a funkciót. A használatához válts olyan csomagra, amelyben ez is elérhető.',
    detail: 'Az admin többi, a csomagodban elérhető funkcióját továbbra is használhatod.',
  };
  if (problem.code === 'quota_exceeded') return {
    icon: 'data_usage', title: 'Elérted a csomagod keretét',
    description: 'Ez a művelet már nem fér bele a jelenlegi csomagod keretébe. Szabadíts fel kapacitást, vagy bővítsd a csomagodat.',
    detail: 'A sikertelen műveletet a bővítés után újra el kell indítanod.',
  };
  const status = subscription?.status ?? problem.subscriptionStatus;
  if (status === 'active' && subscription?.givesAccess) return {
    icon: 'verified', title: 'A Coursys-előfizetésed aktív',
    description: 'Itt kezelheted az oktatói Coursys-előfizetésedet.',
    detail: 'A szüneteltetés a kifizetett időszak végén kezdődik. Addig a hozzáférésed megmarad.',
  };
  if (status === 'trialing' || (status === 'expired' && subscription?.source === 'trial')) return {
    icon: 'hourglass_empty', title: 'Lejárt a próbaidőszakod',
    description: 'Véget ért az ingyenes Coursys-próbád. A folytatáshoz indíts fizetős előfizetést.',
    detail: 'A kártya nélküli próba nem vált automatikusan fizetős előfizetésre.',
  };
  if (status === 'past_due' || status === 'suspended') return {
    icon: 'credit_card_off', title: 'Nem sikerült levonni az előfizetés díját',
    description: 'Az előfizetésed fizetése sikertelen volt. Ellenőrizd a kártyád fedezetét és érvényességét, majd újítsd meg a fizetési módot.',
    detail: subscription?.givesAccess ? 'A türelmi idő alatt még használhatod az admin felületet.' : 'A hozzáférés a sikeres fizetés backendoldali visszaigazolása után áll helyre.',
  };
  if (status === 'paused') return {
    icon: 'pause_circle', title: 'Az előfizetésed szünetel',
    description: 'A szüneteltetés alatt az előfizetéshez kötött funkciók nem érhetők el.',
    detail: 'A folytatáshoz az előfizetésedet újra kell aktiválni.',
  };
  if (status === 'cancel_at_period_end' && subscription?.givesAccess) return {
    icon: 'event_busy', title: 'Az előfizetésed lemondása ütemezve',
    description: 'A hozzáférésed az aktuális időszak végéig megmarad. Utána az előfizetés nem újul meg.',
    detail: 'Az időszak vége előtt díjmentesen visszavonhatod a lemondást.',
  };
  if (status === 'cancelled' || status === 'cancel_at_period_end') return {
    icon: 'event_busy', title: 'Az előfizetésed megszűnt',
    description: 'A lemondott előfizetés hozzáférési időszaka véget ért. A folytatáshoz ismét előfizetés szükséges.',
    detail: 'Sikeres aktiválás után ellenőrizd újra a hozzáférésedet.',
  };
  if (status === 'pending') return {
    icon: 'pending', title: 'Az előfizetésed még nem aktív',
    description: 'A hozzáférésed aktiválásra vár. Ha már fizettél, várd meg a fizetés visszaigazolását, majd ellenőrizd újra.',
    detail: 'A fizetési oldalról való visszatérés önmagában még nem igazolja a sikeres fizetést.',
  };
  return {
    icon: 'lock_clock', title: status === 'expired' ? 'Lejárt az előfizetésed' : 'Az előfizetésed jelenleg nem ad hozzáférést',
    description: 'Az admin funkcióinak használatához aktív Coursys-előfizetés szükséges.',
    detail: 'Ellenőrizd az előfizetésed állapotát, majd próbáld újra a hozzáférést.',
  };
}

@Injectable({ providedIn: 'root' })
export class PlatformAccessService {
  private graceTenant: string | null = null;
  private get storageKey() { return 'coursys_access:' + (localStorage.getItem('tenant_domain') ?? ''); }
  private read(): { problem?: AccessProblem; returnUrl?: string } {
    try { return JSON.parse(sessionStorage.getItem(this.storageKey) ?? '{}'); } catch { return {}; }
  }
  get problem(): AccessProblem | null { return this.read().problem ?? null; }
  set problem(value: AccessProblem | null) {
    const saved = this.read();
    sessionStorage.setItem(this.storageKey, JSON.stringify({ ...saved, problem: value }));
  }
  get returnUrl(): string {
    const url = this.read().returnUrl;
    return url?.startsWith('/admin/') && !url.startsWith(ACCESS_PATH) ? url : '/admin/dashboard';
  }
  set returnUrl(value: string) {
    sessionStorage.setItem(this.storageKey, JSON.stringify({ ...this.read(), returnUrl: value }));
  }
  get graceAcknowledged(): boolean { return this.graceTenant === this.storageKey; }
  set graceAcknowledged(value: boolean) { this.graceTenant = value ? this.storageKey : null; }

  capture(problem: AccessProblem, url: string): void {
    // A concurrent feature failure must not replace an account-wide block.
    if (this.problem?.code !== 'platform_subscription_inactive' || problem.code === 'platform_subscription_inactive') {
      this.problem = problem;
    }
    if (url.startsWith('/admin/') && !url.startsWith(ACCESS_PATH)) this.returnUrl = url;
  }
}
